import { create } from 'zustand';
import type {
  AnalysisStatus,
  AnalysisStep,
  Industry,
  ScanMode,
  UserContext,
  WizardStep,
} from '../types/discovery.types';

interface DiscoveryState {
  // Form state (set on HomePage before navigating to /discovery)
  selectedIndustry: Industry | null;
  userContext: UserContext | null;
  storeReport: boolean;
  // Report mode: 'form_only' (default) or 'sharepoint' (opt-in crawl)
  scanMode: ScanMode;
  selectedSiteIds: string[];

  // Wizard state (consent -> analysis -> download)
  wizardStep: WizardStep;

  // Auth state (API token for authenticated backend calls)
  apiToken: string | null;

  // Analysis state
  analysisId: string | null;
  status: AnalysisStatus | null;
  currentStep: AnalysisStep | null;
  stepsCompleted: AnalysisStep[];
  progressPct: number;
  message: string;
  error: string | null;
  consentUrl: string | null;

  // Report state
  reportReady: boolean;
  reportStored: boolean;
  fileName: string | null;
  /** Object URL for the inline-returned .docx (synchronous generation). */
  reportObjectUrl: string | null;
  score: number | null;
  tier: string | null;
  aiContentUsed: boolean;
  sitesAnalysed: number | null;
  librariesAnalysed: number | null;
  filesAnalysed: number | null;

  // Actions
  setFormData: (
    industry: Industry,
    userContext: UserContext,
    storeReport: boolean,
    scanMode?: ScanMode,
    selectedSiteIds?: string[]
  ) => void;
  setWizardStep: (step: WizardStep) => void;
  setApiToken: (token: string) => void;
  startAnalysis: (analysisId: string) => void;
  /** Enter the in-flight generating state (indeterminate progress) before the
   *  synchronous start-analysis request resolves. */
  beginGenerating: () => void;
  updateProgress: (
    status: AnalysisStatus,
    currentStep: AnalysisStep | null,
    stepsCompleted: AnalysisStep[],
    progressPct: number,
    message: string,
    error: string | null,
    consentUrl?: string | null
  ) => void;
  setReport: (
    fileName: string,
    score: number | null,
    tier: string | null,
    aiContentUsed: boolean,
    sitesAnalysed?: number | null,
    librariesAnalysed?: number | null,
    filesAnalysed?: number | null
  ) => void;
  /** Mark generation complete with the inline-returned report (synchronous flow). */
  completeAnalysis: (payload: {
    analysisId: string;
    fileName: string;
    score: number | null;
    tier: string | null;
    aiContentUsed: boolean;
    reportStored: boolean;
    reportObjectUrl: string;
  }) => void;
  setReportStored: (stored: boolean) => void;
  reset: () => void;
}

const initialState = {
  selectedIndustry: null as Industry | null,
  userContext: null as UserContext | null,
  storeReport: false,
  scanMode: 'form_only' as ScanMode,
  selectedSiteIds: [] as string[],
  wizardStep: 'analysis' as WizardStep,
  apiToken: null as string | null,
  analysisId: null as string | null,
  status: null as AnalysisStatus | null,
  currentStep: null as AnalysisStep | null,
  stepsCompleted: [] as AnalysisStep[],
  progressPct: 0,
  message: '',
  error: null as string | null,
  consentUrl: null as string | null,
  reportReady: false,
  reportStored: false,
  fileName: null as string | null,
  reportObjectUrl: null as string | null,
  score: null as number | null,
  tier: null as string | null,
  aiContentUsed: false,
  sitesAnalysed: null as number | null,
  librariesAnalysed: null as number | null,
  filesAnalysed: null as number | null,
};

export const useDiscoveryStore = create<DiscoveryState>((set) => ({
  ...initialState,

  setFormData: (industry, userContext, storeReport, scanMode = 'form_only', selectedSiteIds = []) =>
    set({ selectedIndustry: industry, userContext, storeReport, scanMode, selectedSiteIds }),

  setWizardStep: (step) => set({ wizardStep: step }),

  setApiToken: (token) => set({ apiToken: token }),

  startAnalysis: (analysisId) =>
    set({
      analysisId,
      status: 'pending',
      wizardStep: 'analysis',
      error: null,
    }),

  beginGenerating: () =>
    set({
      status: 'generating_ai_content',
      currentStep: 'generate_ai_content',
      stepsCompleted: ['authenticate'],
      // -1 => indeterminate bar: synchronous generation gives no mid-flight steps.
      progressPct: -1,
      message: 'Analysing your responses and writing your report…',
      error: null,
      consentUrl: null,
      wizardStep: 'analysis',
    }),

  updateProgress: (status, currentStep, stepsCompleted, progressPct, message, error, consentUrl) =>
    set({ status, currentStep, stepsCompleted, progressPct, message, error, consentUrl: consentUrl ?? null }),

  setReport: (fileName, score, tier, aiContentUsed, sitesAnalysed, librariesAnalysed, filesAnalysed) =>
    set({
      reportReady: true, fileName, score, tier, aiContentUsed, wizardStep: 'download',
      sitesAnalysed: sitesAnalysed ?? null,
      librariesAnalysed: librariesAnalysed ?? null,
      filesAnalysed: filesAnalysed ?? null,
    }),

  completeAnalysis: (payload) =>
    set({
      analysisId: payload.analysisId,
      status: 'completed',
      reportReady: true,
      wizardStep: 'download',
      fileName: payload.fileName,
      reportObjectUrl: payload.reportObjectUrl,
      score: payload.score,
      tier: payload.tier,
      aiContentUsed: payload.aiContentUsed,
      reportStored: payload.reportStored,
      error: null,
    }),

  setReportStored: (stored) => set({ reportStored: stored }),

  reset: () =>
    set((state) => {
      // Release the inline report blob so its memory isn't leaked across runs.
      if (state.reportObjectUrl) URL.revokeObjectURL(state.reportObjectUrl);
      return initialState;
    }),
}));
